import React, { ChangeEvent, useEffect, useState } from 'react'
import { Button } from './ui/button'
import {
  setClonedImages,
  setDeleteList,
  setOfferImages,
} from '@/app/globalRedux/myfructidor/offersStore'
import { AppDispatch, RootState } from '@/app/globalRedux/store'
import { useDispatch, useSelector } from 'react-redux'
import { Label } from './ui/label'
import { useTranslation } from '@/app/i18n/client'
import Image from 'next/image'

interface ImageUploaderProps {
  imageArray?: { id: string; url: string }[]
  setImage?: (a: any) => void
  clonedArray?: { id: string; url: string }[]
  setDeleteArray?: (a: { id: string; url: string }[]) => void
  setClonedArray?: (a: { id: string; url: string }[]) => void
  isNews?: boolean
}

const ImageUploader = ({
  imageArray,
  setImage,
  setDeleteArray,
  setClonedArray,
  clonedArray,
  isNews,
}: ImageUploaderProps) => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const { defaultImage, clonedImages } = useSelector((state: RootState) => {
    return state.offerData
  })

  const { images } = useSelector((state: RootState) => {
    return state.newsData.newsDetails
  })

  const [imageList, setImageList] = useState<string[]>([])
  const [deleteImgList, setDeleteImgList] = useState<
    { id: string; url: string }[]
  >([])
  const [error, setError] = useState<string>('')

  const maxImages = 5
  useEffect(() => {
    if (!imageArray) {
      const urls = isNews
        ? images?.map((img: any) => img.url)
        : defaultImage?.map((img: any) => img.url)
      setImageList(urls || [])
    } else {
      const urls = imageArray?.map((img: any) => img.url)
      setClonedArray ? setClonedArray(imageArray ?? []) : ''
      setImageList(urls)
    }
  }, [defaultImage, imageArray, images])

  const handleButtonClick = (): void => {
    const fileInput = document.getElementById(
      'offer-images'
    ) as HTMLInputElement
    if (fileInput) {
      fileInput.click()
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setError('')
    const files = event.target.files
    if (files && files.length > 0) {
      if (imageList.length + files.length > maxImages) {
        setError(`Maximum ${maxImages} files allowed`)
        return
      }
      const newImages: string[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (file.type?.startsWith('image/')) {
          if (file.size <= 1 * 1000 * 1000) {
            const imageURL = URL.createObjectURL(file)
            newImages.push(imageURL)
          } else {
            setError('File size exceeds the limit of 1MB')
            return
          }
        } else {
          setError('Invalid file type.')
        }
      }
      setImageList((prevImages) => [...prevImages, ...newImages])
    }
  }

  const handleRemovePhoto = (index: number): void => {
    const removedImage = imageList[index]
    setImageList((prevImages) => prevImages.filter((_, i) => i !== index))

    let removedImageInfo: { id: string; url: string } | undefined

    if (!imageArray) {
      removedImageInfo = defaultImage?.find(
        (img: { id: string; url: string }) => img.url === removedImage
      )
      dispatch(
        setClonedImages(
          clonedImages?.filter(
            (img: { id: string; url: string }) => img.url !== removedImage
          )
        )
      )
    } else {
      removedImageInfo = imageArray?.find(
        (img: { id: string; url: string }) => img.url === removedImage
      )
      setClonedArray &&
        setClonedArray(
          clonedArray
            ? clonedArray?.filter(
                (img: { id: string; url: string }) => img.url !== removedImage
              )
            : []
        )
    }

    if (removedImageInfo) {
      setDeleteImgList((prevDeleteList: any) => [
        ...prevDeleteList,
        removedImageInfo,
      ])
    }
  }
  useEffect(() => {
    if (!imageArray) {
      dispatch(setOfferImages(imageList))
    } else {
      setImage ? setImage(imageList) : ''
    }
  }, [imageList])

  useEffect(() => {
    if (!imageArray) {
      dispatch(setDeleteList(deleteImgList))
    } else {
      setDeleteArray ? setDeleteArray(deleteImgList) : ''
    }
  }, [deleteImgList])

  return (
    <>
      <div className="flex w-full items-center gap-6 border-2 border-[#e0e0e0] rounded p-3.5  mobile:gap-3">
        <div className="flex flex-col relative mobile:self-start">
          <Label
            htmlFor="offer-images"
            className="relative h-[50px] w-[57px] shadow-[0_0_6px_0_rgba(0,0,0,0.18)] p-1 block"
          >
            <Image
              loading="lazy"
              className="h-[43px] w-[57px] object-contain"
              src="/assets/Icons/noImage/noImage1.svg"
              alt="Selected-images"
              width={100}
              height={100}
            />
          </Label>
          <span className="w-4 h-4 bg-primary absolute -top-2 -right-1 rounded-full flex justify-center items-center p-2.5 text-xs text-white">
            {imageList?.length ?? 0}
          </span>
          <Button
            variant="smallBtn"
            className="text-xs mobile:p-[1]"
            onClick={handleButtonClick}
          >
            <Image
              loading="lazy"
              width={12}
              height={12}
              src="/assets/Icons/uploadIcon.svg"
              alt="upload-icon"
            />
            {t('Upload')}
          </Button>
        </div>
        <div className="flex flex-col gap-1">
          <div className="flex gap-3 items-center flex-wrap">
            {imageList &&
              imageList.map((image: string, index: number) => {
                return (
                  <div key={`img-index_${index}`} className="relative">
                    <div className="w-[50px] h-[50px] mobile:w-[47px] mobile:h-[47px] bg-cover">
                      <Image
                        loading="lazy"
                        src={image}
                        alt="Uploaded Image"
                        className="object-cover w-full h-full"
                        width={50}
                        height={50}
                      />
                    </div>
                    <Image
                      loading="lazy"
                      width={12}
                      height={12}
                      src="/assets/Icons/closeIcon.svg"
                      alt="close-icon"
                      className="absolute top-0 right-0 p-0.5 cursor-pointer"
                      onClick={() => handleRemovePhoto(index)}
                    />
                  </div>
                )
              })}
          </div>

          <p className="font-10 text-gray_text">
            {maxImages}{' '}
            {t(
              'Photos max, must be in PNG, JPG, JPEG, or WEBP format, with a total maximum size of 3 MB'
            )}
          </p>
        </div>
        <input
          type="file"
          id="offer-images"
          className="hidden"
          onChange={handleFileChange}
          accept="image/png, image/jpeg, image/webp, image/jpg"
          multiple
        />
      </div>
      <span className="text-required_color">{t(error)}</span>
    </>
  )
}

export default ImageUploader
